import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { authApi } from '../api';

/**
 * Theme is a `data-theme` attribute on <html>; every colour resolves through
 * tokens.css, so switching costs one attribute write and no re-render of styles.
 *
 * The choice lives on the **user record**, not in localStorage. Someone who picks the
 * light theme gets it on the next machine they sign in from, and does not lose it by
 * clearing their browser data — which is the person most likely to have needed a
 * specific theme in the first place.
 *
 * `system` is a real third option, stored server-side as an empty string, so "has not
 * chosen" stays distinguishable from "chose light". Collapsing them would mean
 * overriding the OS preference of everyone who never touched the setting.
 */

const ThemeContext = createContext(null);
export const THEMES = ['system', 'light', 'dark'];

/** Survives a remount within the tab, so a re-render never flashes the wrong theme. */
let remembered = 'system';

function systemTheme() {
  if (typeof window === 'undefined' || !window.matchMedia) return 'dark';
  return window.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark';
}

export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(remembered);
  const [resolved, setResolved] = useState(() =>
    remembered === 'system' ? systemTheme() : remembered,
  );

  useEffect(() => {
    remembered = theme;
    const next = theme === 'system' ? systemTheme() : theme;
    setResolved(next);
    document.documentElement.dataset.theme = next;
  }, [theme]);

  // Follow the OS while set to `system`. Without this the page keeps whatever the
  // OS was at load time, so a machine that switches at sunset does not.
  useEffect(() => {
    if (theme !== 'system' || !window.matchMedia) return undefined;
    const query = window.matchMedia('(prefers-color-scheme: light)');
    const onChange = () => {
      const next = systemTheme();
      setResolved(next);
      document.documentElement.dataset.theme = next;
    };
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, [theme]);

  /**
   * Apply a theme that came *from* the server. Deliberately does not write back:
   * echoing it would turn every sign-in into a redundant POST, and a failure there
   * would surface as an error the user did not cause.
   */
  const applyRemote = useCallback((next) => {
    setThemeState(THEMES.includes(next) ? next : 'system');
  }, []);

  // A theme change is worth persisting but never worth blocking on, and never worth
  // an error toast: the visible part already happened locally. A 401 here is normal
  // — the login screen has a theme too, and nobody is signed in yet.
  const persisting = useRef(false);
  const persist = useCallback((next) => {
    if (persisting.current) return;
    persisting.current = true;
    authApi
      .setTheme(next)
      .catch(() => {})
      .finally(() => {
        persisting.current = false;
      });
  }, []);

  const setTheme = useCallback(
    (next, { persist: shouldPersist = true } = {}) => {
      if (!THEMES.includes(next)) return;
      setThemeState(next);
      if (shouldPersist) persist(next);
    },
    [persist],
  );

  const toggle = useCallback(() => {
    // Toggling from `system` picks the opposite of what is currently showing, which
    // is what someone reaching for the button means — not "cycle to light".
    const current = theme === 'system' ? systemTheme() : theme;
    setTheme(current === 'dark' ? 'light' : 'dark');
  }, [theme, setTheme]);

  const value = useMemo(
    () => ({ theme, resolved, setTheme, applyRemote, toggle, themes: THEMES }),
    [theme, resolved, setTheme, applyRemote, toggle],
  );
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) throw new Error('useTheme must be used inside ThemeProvider');
  return context;
}
